import {useCallback, useEffect, useMemo, useState} from "react";
import styled from "@emotion/styled";
import {Text} from "@blueprintjs/core";
import {DangerButton, SelectedInfo} from "./styles/styleds";
import {ManifestFilter, ManifestStop} from "../../generated/graphql";
import ManifestDataProvider from "../manifest/providers/ManifestDataProvider";
import ManifestDriverPanel from "../manifest/list/ManifestDriverPanel";
import {ManifestFilterTypePayload} from "../../providers/AssignmentViewReducer";
import {ManifestFilterType, ManifestFilterState} from "../manifest/list/components/manifest-filter/ManifestFilterState";
import {DateRange} from "@blueprintjs/datetime2";
import {OptimizedStatusTypes, StatusType} from "../manifest/list/components/manifest-filter/ManifestFilter";
import {buildManifestStatusFilter, buildOptimizedStatusFilter} from "../../views/UnassignedJobsView";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../providers/DispatchStationProvider";
import {useDispatchGroupContext} from "../../providers/DispatchGroupProvider";
import {
  buildManifestDriverGroupFilter,
  getMaxStopCount,
  hasMultiPickupDeliveryJobs,
  hasTooManyStops
} from "../../utils/ManifestUtils";
import {useAssignmentState, useAssignmentDispatch} from "../../providers/AssignmentProdiver";
import {useAssignmentViewState} from "../../providers/AssignmentViewProvider";
import {useManifestDetailsDispatch} from "../../providers/ManifestDetailsProvider";
import {makeQueryStringFilter} from "../../utils/FilterUtils";
import {
  RoutePlanningActionType,
  useRoutePlanningContext,
  useRoutePlanningDispatch
} from "../../providers/RoutePlanningProvider";
import {AppToasterTopLeft} from "../../utils/toasters/toaster";
import {Intent} from "@blueprintjs/core";
import {COLORS, Constants, UserPreferences} from "../../constants/constants";
import {extractJsonPref, usePreferenceContext} from "../../providers/PreferenceProvider";
import {isArray} from "lodash";
import {ManifestFilterPreference} from "../manifest/list/ManifestDriverPanel";
import {StopStatusCategory} from "../manifest/list/components/manifest-filter/types/StopStatusManifestFilter";

const ACTIVE_STOP_STATUSES = ["A", "Q"];

/** Extra fields the IP map grid needs beyond ManifestDataProvider's BASE_FIELDS. */
const IP_ADDITIONAL_FIELDS = [
  "stops.name",
  "stops.dispatchZone",
  "stops.see",
  "stops.phone",
  "stops.note",
  "stops.room",
  "stops.job.jobId",
  "stops.job.jobNumber",
  "stops.job.alias",
  "stops.job.pieces",
  "stops.job.weight",
  "stops.job.routeNumber",
  "stops.job.site.code",
  "stops.order.orderId",
  "stops.order.service",
  "stops.order.auth",
  "stops.order.alias",
  "stops.order.caller",
  "stops.order.callerPhone",
  "stops.order.notes",
  "stops.order.declaredValue",
  "stops.order.codAmount",
  "stops.order.customer.name",
  "stops.order.customer.customerCode",
  "stops.order.customer.notes",
  "stops.order.priority.description",
  "stops.order.orderSkills.id",
  "stops.order.orderSkills.name",
  "stops.order.requirements.id",
  "stops.order.requirements.code",
  "stops.order.requirements.name",
  "stops.order.createDate",
  "stops.order.user.userId",
  "stops.order.user.userName"
];

const IPManifestSelector = () => {
  const rpDispatch = useRoutePlanningDispatch();
  const {selectedDrivers} = useRoutePlanningContext();
  const assignmentState = useAssignmentState();
  const assignmentDispatch = useAssignmentDispatch();
  const assignmentViewState = useAssignmentViewState();
  const manifestDetailsDispatch = useManifestDetailsDispatch();
  const dispatchStationState = useDispatchStationContext();
  const {driverGroups} = useDispatchGroupContext();
  const {userPreferences} = usePreferenceContext();

  const handleManifestDetailsOpen = useCallback(
    (manifestDriverId: number, driverId: number) => {
      manifestDetailsDispatch({
        type: "SET_MANIFEST_DETAILS",
        payload: {manifestDriverId, driverId}
      });
    },
    [manifestDetailsDispatch]
  );

  // Initialize from saved user preferences so the first query matches what the
  // ManifestDriverPanel will apply on mount — avoids an unfiltered initial fetch.
  const savedFilterPref = extractJsonPref(userPreferences, UserPreferences.manifestFilterHeader)?.value as
    | ManifestFilterPreference
    | undefined;
  const [statusTypes, setStatusTypes] = useState<StatusType[]>(() => savedFilterPref?.statusTypes ?? []);
  const [selectedOptimizedStatusTypes, setSelectedOptimizedStatusTypes] = useState<OptimizedStatusTypes[]>(
    () => savedFilterPref?.optimizedStatusTypes ?? []
  );
  const [manifestQuery, setManifestQuery] = useState<string>("");
  const [filterState, setFilterState] = useState<ManifestFilterState>(() => {
    let state = new ManifestFilterState();
    // Date range
    const savedDateRange = extractJsonPref(userPreferences, UserPreferences.manifestDateRangeHeader)?.value;
    if (savedDateRange && isArray(savedDateRange) && savedDateRange.length > 1) {
      const dateRange = savedDateRange.map((d: string) => new Date(d)) as DateRange;
      state = state.addManifestFilterValue(dateRange, ManifestFilterType.DATE_RANGE);
    }
    // Driver groups
    const savedGroupIds = extractJsonPref(userPreferences, UserPreferences.manifestGroupHeader)?.value;
    if (savedGroupIds && isArray(savedGroupIds)) {
      savedGroupIds.forEach((key: number) => {
        state = state.addManifestFilterValue(key, ManifestFilterType.DRIVER_GROUP);
      });
    }
    // Empty/New manifest filters
    if (savedFilterPref) {
      const filterPayloads: ManifestFilterTypePayload[] = [];
      if (savedFilterPref.emptyActive) {
        filterPayloads.push({filterType: ManifestFilterType.STOP_STATUS, value: StopStatusCategory.COMPLETE});
      }
      if (savedFilterPref.newActive) {
        filterPayloads.push({filterType: ManifestFilterType.MANIFEST_AGE, value: Constants.MANIFEST_NEW_AGE});
      }
      if (filterPayloads.length > 0) {
        state = state.addManifestFilterValues(filterPayloads);
      }
    }
    return state;
  });

  // Detect card click → toggle in RoutePlanningProvider.
  // Every click dispatches SetSelectedManifest → new assignmentState ref → effect fires.
  // Always clear selectedManifest after processing so the next click on the same card
  // creates a fresh state change (undefined → manifest).
  useEffect(() => {
    const manifest = assignmentState.selectedManifest;
    if (!manifest) return;

    const manifestDriverId = manifest.manifestDriverId;
    const alreadySelected = selectedDrivers.some((d) => d.manifestDriverId === manifestDriverId);

    if (alreadySelected) {
      rpDispatch({type: RoutePlanningActionType.REMOVE_MANIFEST, payload: manifestDriverId});
    } else if (hasTooManyStops(manifest)) {
      const maxStopCount = getMaxStopCount(manifest);
      AppToasterTopLeft.show({
        message: `This manifest ${
          maxStopCount !== undefined ? `exceeds the maximum of ${maxStopCount} stops` : "has too many stops"
        } and cannot be selected for route planning.`,
        intent: Intent.WARNING
      });
    } else if (hasMultiPickupDeliveryJobs(manifest)) {
      AppToasterTopLeft.show({
        message:
          "This manifest contains jobs with multiple pickups and multiple deliveries, which is not supported for route planning.",
        intent: Intent.WARNING
      });
    } else if ((manifest.stops ?? []).some((s) => s.jobStopStatus === "N")) {
      AppToasterTopLeft.show({
        message:
          "This manifest contains stops with an unsupported status. Dispatch only supports stops in the Queued, Accepted, and Cleared (Completed) statuses.",
        intent: Intent.WARNING
      });
    } else {
      // Check if this manifest's driver is already selected as a New Driver
      // (not as a manifest driver — multiple manifests of the same driver are allowed)
      const driverId = manifest.driver?.driverId;
      const isManifestDriver = driverId
        ? selectedDrivers.some((d) => !!d.manifestDriverId && d.driverId === driverId)
        : false;
      const conflictDriver =
        driverId && !isManifestDriver
          ? selectedDrivers.find((d) => !d.manifestDriverId && d.driverId === driverId)
          : undefined;
      if (conflictDriver) {
        const driverLabel =
          [conflictDriver.name, conflictDriver.driverCode].filter(Boolean).join(" - ") || `Driver ${driverId}`;
        AppToasterTopLeft.show({
          message: (
            <span>
              <i>{driverLabel}</i> is already selected as a New Driver. Remove them first to select their manifest.
            </span>
          ),
          intent: Intent.WARNING
        });
      } else {
        const skillsStr = manifest.driver?.skills?.map((s) => s.id).join(",") ?? "";
        rpDispatch({
          type: RoutePlanningActionType.ADD_MANIFEST,
          payload: {
            manifestDriverId,
            driverId: driverId ?? 0,
            driverName: manifest.driver?.name ?? "",
            driverCode: manifest.driver?.code ?? "",
            vehicleDescription: manifest.vehicleType?.description ?? null,
            skills: skillsStr,
            stops: (manifest.stops ?? []) as ManifestStop[]
          }
        });
      }
    }

    // Always clear so the next click on the same card triggers a new state change
    assignmentDispatch({type: "ClearSelections"});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally narrow: we only care when a new manifest is selected, not the full
    // assignmentState object — reacting to the full object would fire on unrelated state changes.
  }, [assignmentState.selectedManifest]);

  // Sync selected manifests → AssignmentProvider's highlightManifests
  // so cards show the same border as when dragging a job onto them.
  useEffect(() => {
    const ids = selectedDrivers.filter((d) => !!d.manifestDriverId).map((d) => d.manifestDriverId!);
    assignmentDispatch({type: "SetHighlightManifest", payload: ids.length > 0 ? ids : []});
    return () => {
      assignmentDispatch({type: "SetHighlightManifest", payload: []});
    };
  }, [selectedDrivers, assignmentDispatch]);

  // --- ManifestDriverPanel callbacks (mirror ManifestDetailsManifestSelector) ---
  const onFilterSelected = useCallback(
    (filterPayloads: ManifestFilterTypePayload[], oldFilterState?: ManifestFilterTypePayload[]) => {
      setFilterState((prev) => {
        let state = prev;
        if (oldFilterState) {
          state = state.clearManifestFilterTypes(oldFilterState.map((f) => f.filterType));
        }
        if (filterPayloads.length === 0) {
          state = state.addManifestFilterValue(undefined, ManifestFilterType.NONE);
        } else {
          state = state.addManifestFilterValues(filterPayloads);
        }
        return state.copy();
      });
    },
    []
  );

  const onFilterDateChanged = useCallback(
    (dateRange: DateRange | undefined, _selectedDateRange: DateRange | undefined) => {
      setFilterState((prev) => {
        return prev.replaceManifestFilterValue(dateRange, ManifestFilterType.DATE_RANGE).copy();
      });
    },
    []
  );

  const onSelectDriverGroup = useCallback((key: number, arrayRef: React.MutableRefObject<number[]>) => {
    arrayRef.current.push(key);
    setFilterState((prev) => {
      return prev.addManifestFilterValue(key, ManifestFilterType.DRIVER_GROUP).copy();
    });
  }, []);

  const onUnselectDriverGroup = useCallback((key: number, arrayRef: React.MutableRefObject<number[]>) => {
    arrayRef.current = arrayRef.current.filter((d) => d !== key);
    setFilterState((prev) => {
      return prev.removeManifestFilterValue(key, ManifestFilterType.DRIVER_GROUP).copy();
    });
  }, []);

  const onReplaceDriverGroup = useCallback((arrayRef: React.MutableRefObject<number[]>) => {
    setFilterState((prev) => {
      return prev.replaceManifestFilterValues(arrayRef.current, ManifestFilterType.DRIVER_GROUP).copy();
    });
  }, []);

  const onUnselectAllDriverGroup = useCallback(() => {
    setFilterState((prev) => {
      return prev.clearManifestFilterType(ManifestFilterType.DRIVER_GROUP).copy();
    });
  }, []);

  const onHandleManifestQuery = useCallback((query: string) => {
    setManifestQuery(query);
  }, []);

  const buildFilter = useCallback((): ManifestFilter => {
    const result: ManifestFilter = {manifest: {and: []}};

    if (statusTypes.length > 0) {
      buildManifestStatusFilter(result, statusTypes);
    } else {
      result.manifest?.and?.push({
        or: [{manifestStatus: {match: "A"}}, {manifestStatus: {match: "F"}}]
      });
    }
    buildOptimizedStatusFilter(result, selectedOptimizedStatusTypes);

    if (hasSelectedDispatchStations(dispatchStationState)) {
      result.manifest?.and?.push(buildManifestDriverGroupFilter(driverGroups));
    }

    // Apply ManifestDriverPanel's filter selections (date, groups, age, etc.)
    filterState.appendToFilter(result);

    if (manifestQuery) {
      result.queryString = makeQueryStringFilter(manifestQuery, [
        "driver.name",
        "driver.code",
        "manifestDriverId",
        "stops.job.jobNumber"
      ]);
    }

    return result;
  }, [statusTypes, selectedOptimizedStatusTypes, dispatchStationState, driverGroups, manifestQuery, filterState]);

  // S4: memoize filter to avoid creating a new object on every render
  const filter = useMemo(() => buildFilter(), [buildFilter]);
  // R2: extract manifest count to avoid duplicate filter passes in JSX
  const manifestCount = selectedDrivers.filter((d) => !!d.manifestDriverId).length;

  return (
    <PanelWrapper>
      <ManifestDataProvider
        additionalFields={IP_ADDITIONAL_FIELDS}
        filter={filter}
        sort={{
          field: assignmentViewState.manifestSort.property,
          direction: assignmentViewState.manifestSort.sortDirection
        }}
      >
        <ManifestPanelContainer id="ip-manifest-panel">
          <ManifestDriverPanel
            statusTypes={statusTypes}
            setStatusTypes={setStatusTypes}
            selectedOptimizedStatusTypes={selectedOptimizedStatusTypes}
            setSelectedOptimizedStatusTypes={setSelectedOptimizedStatusTypes}
            hideCreateManifestButton={true}
            skipSavePreferences
            onManifestDetailsDrawerOpen={() => {}}
            filterSelected={onFilterSelected}
            filterDateChanged={onFilterDateChanged}
            selectDriverGroup={onSelectDriverGroup}
            unselectDriverGroup={onUnselectDriverGroup}
            replaceDriverGroup={onReplaceDriverGroup}
            unselectAllDriverGroup={onUnselectAllDriverGroup}
            handleManifestQuery={onHandleManifestQuery}
          />
          <SelectedInfo>
            <Text>{manifestCount} selected</Text>
            {manifestCount > 0 && (
              <DangerButton
                data-testid="clear-selected-manifests-button"
                variant="minimal"
                intent="danger"
                onClick={() => {
                  selectedDrivers
                    .filter((d) => !!d.manifestDriverId)
                    .forEach((d) =>
                      rpDispatch({type: RoutePlanningActionType.REMOVE_MANIFEST, payload: d.manifestDriverId!})
                    );
                }}
              >
                Clear
              </DangerButton>
            )}
          </SelectedInfo>
        </ManifestPanelContainer>
      </ManifestDataProvider>
    </PanelWrapper>
  );
};

export default IPManifestSelector;

const PanelWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ManifestPanelContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;

  /* Remove background + shadow from ManifestDriverViewsContainer */
  > div {
    background: transparent !important;
    box-shadow: none !important;
    flex: 1;
    min-height: 0;
  }

  > div:first-child {
    > *:nth-child(1) {
      padding: 0;
      margin-bottom: 8px;
    }
    > *:not(:first-child) {
      border-left: 1px solid ${COLORS.COLOR_BORDER};
      border-right: 1px solid ${COLORS.COLOR_BORDER};
    }
    > *:nth-child(2) {
      border-top: 1px solid ${COLORS.COLOR_BORDER};
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }
    > *:last-child {
      background: ${COLORS.WHITE};
      border-top: 1px solid ${COLORS.COLOR_BORDER};
      border-bottom: 1px solid ${COLORS.COLOR_BORDER};
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      padding: 0 12px;

      > :last-child {
        height: 48px;
        border-bottom: none;
      }
    }
  }
`;
